import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { useGetSearchProductListQuery } from "../../../utils/api/product/product.api";
import { formatCurrency, getImage, matchesProductTitleSearch } from "../../../utils/Utils";
import { getCatalogPriceInfo } from "../../../utils/catalogProductPrice";
import { useShopTvaSettings } from "../../../catalog/useShopDevise";
import { AlertInfo } from "../../common/Alert";
import Empty from "../../../assets/images/appImages/category.png";
import {
  buildCatalogPath,
  getCatalogVendeur,
} from "../../../catalog/catalogTenant";

interface SearchFormProps {
  useTemplateStyle?: boolean;
  placeholder?: string;
}

function SearchForm({ useTemplateStyle = false, placeholder }: SearchFormProps) {
  const Vendeur = getCatalogVendeur();
  const tvaSettings = useShopTvaSettings();
  const [isDivVisible, setIsDivVisible] = useState(false);
  const { slug } = useParams<{ slug: string }>();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setQuery("");
    setDebouncedQuery("");
  }, [slug]);

  const shouldSearch = debouncedQuery.length >= 3;
  const { data = { results: [] }, isFetching } = useGetSearchProductListQuery(
    {
      q: debouncedQuery,
      limit: 20,
    },
    { skip: !shouldSearch },
  );

  const results = (data?.results ?? []).filter((item: any) =>
    matchesProductTitleSearch(item, debouncedQuery),
  );

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsDivVisible(true);
  };

  const goToSearchPage = () => {
    const q = query.trim();
    if (q.length < 3) return;
    navigate(`${buildCatalogPath("/recherche")}?q=${encodeURIComponent(q)}`);
    setIsDivVisible(false);
  };

  const renderResults = () => {
    if (!isDivVisible || query.trim().length < 3) return null;

    return (
      <div className={useTemplateStyle ? undefined : "list-container"} style={useTemplateStyle ? {
        position: "absolute",
        top: "100%",
        left: 0,
        width: "100%",
        zIndex: 999,
      } : undefined}>
        <ul className="search-result-container">
          {isFetching && (
            <li className="search-result-item">
              <AlertInfo variant="catalog" message="Recherche..." />
            </li>
          )}
          {!isFetching &&
            results.map((item: any) => (
              <li className="search-result-item" key={item?.id}>
                <Link
                  to={buildCatalogPath(`/produit/${item?.slug}`)}
                  state={item}
                  className="no-link"
                  onClick={() => {
                    setIsDivVisible(false);
                    setQuery("");
                  }}
                >
                  <div className="row align-items-center">
                    <div className={useTemplateStyle ? "col-sm-3 m-b-20 m-r-9" : "col-sm-3 m-b-20 m-r-9"}>
                      <img
                        loading="lazy"
                        className={useTemplateStyle ? undefined : "img-thumbs-commande-item"}
                        src={getImage(
                          item?.type === "taille_unique"
                            ? item?.images?.[0]?.image
                            : item?.variations?.[0]?.images?.[0]?.image ?? Empty,
                        )}
                        alt={item?.nom}
                        style={{ width: "75px", height: "75px" }}
                      />
                    </div>
                    <div className="pl-lg-0 col-sm-8 m-b-20">
                      <p className="name-prduct-search fs-15">{item?.nom}</p>
                      {!!item?.id && (
                        <p className="price-solde-product-item mb-0">
                          {formatCurrency(
                            getCatalogPriceInfo(item, tvaSettings).displayPrice,
                            Vendeur?.devise,
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          {!isFetching && results.length === 0 && (
            <li className="search-result-item">
              <AlertInfo variant="catalog" message="Aucun résultat trouvé!" />
            </li>
          )}
          {!isFetching && results.length > 0 && (
            <li className="search-result-item">
              <button
                type="button"
                className="btn btn-link no-link p-0"
                onMouseDown={(e) => e.preventDefault()}
                onClick={goToSearchPage}
              >
                Voir tous les résultats
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  };

  if (useTemplateStyle) {
    return (
      <div
        className="template-navbar__search-wrapper"
        style={{ position: "relative", width: "100%" }}
      >
        <span className="template-navbar__search-icon">
          <FiSearch size={16} color="#d92d06" />
        </span>

        <input
          type="text"
          className="template-search-input"
          placeholder={placeholder || "Recherchez ici"}
          value={query}
          onChange={handleChangeSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goToSearchPage();
            }
          }}
          onBlur={() => setTimeout(() => setIsDivVisible(false), 300)}
        />
        {renderResults()}
      </div>
    );
  }

  return (
    <div className="input-group-search relative-search-form">
      <div className="input-group-search-inner">
        <input
          type="text"
          className="form-control search-field"
          value={query}
          onChange={handleChangeSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goToSearchPage();
            }
          }}
          onBlur={() => setTimeout(() => setIsDivVisible(false), 300)}
          placeholder={placeholder || "Rechercher un produit"}
        />
        <span className="input-group-btn input-group-btn-right" aria-hidden="true">
          <i className="fa fa-search" />
        </span>
        {renderResults()}
      </div>
    </div>
  );
}

export default SearchForm;
